import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { WithdrawalRiskPolicyDTO } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid withdrawal risk policy': '提现风控参数无效',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useWithdrawalRiskPolicy() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<WithdrawalRiskPolicyDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sameTargetUserThreshold, setSameTargetUserThreshold] = useState(0);
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [telegramBotToken, setTelegramBotToken] = useState('');
  const [telegramChatID, setTelegramChatID] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getWithdrawalRiskPolicy();
      setData(result);
      setSameTargetUserThreshold(result.same_target_user_threshold);
      setTelegramEnabled(Boolean(result.telegram_enabled));
      setTelegramBotToken('');
      setTelegramChatID(result.telegram_chat_id ?? '');
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const result = await settingsService.updateWithdrawalRiskPolicy({
        same_target_user_threshold: Number.isFinite(sameTargetUserThreshold)
          ? sameTargetUserThreshold
          : 0,
        telegram_enabled: telegramEnabled,
        telegram_bot_token: telegramBotToken.trim(),
        telegram_chat_id: telegramChatID.trim(),
      });
      setData(result);
      setSameTargetUserThreshold(result.same_target_user_threshold);
      setTelegramEnabled(Boolean(result.telegram_enabled));
      setTelegramBotToken('');
      setTelegramChatID(result.telegram_chat_id ?? '');
      addToast('success', '提现风控配置已保存');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  }, [addToast, sameTargetUserThreshold, telegramBotToken, telegramChatID, telegramEnabled]);

  return {
    data,
    loading,
    saving,
    error,
    sameTargetUserThreshold,
    setSameTargetUserThreshold,
    telegramEnabled,
    setTelegramEnabled,
    telegramBotToken,
    setTelegramBotToken,
    telegramChatID,
    setTelegramChatID,
    save,
    reload: load,
  };
}
